import { Injectable } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';
import { menu } from '@routes/menu';
import { DashboardApiService } from '@shared/services/api/dashboard/dashboard-api.service';
import { InvitedeskApiService } from '@shared/services/api/legacy/invitedesk-api.service';
import { ErrorResponse as ErrorResponseLegacy, SuccessResponse as SuccessResponseLegacy } from '@shared/services/api/legacy/response';
import { ErrorResponse, SuccessResponse } from '@shared/services/api/response';
import { IPermission } from '@shared/services/api/legacy/settings-service/setting-model';
import { PermissionService } from '@shared/services/permission-service/permission.service';
import { ICustomFonts } from '@shared/services/api/dashboard/customfonts-service/customfonts-model';
import { StyleService } from '@shared/services/style/style.service';
import { IBrandingStyleResponse } from '@shared/services/api/dashboard/branding-service/branding-model';
import { AuthService } from '@shared/services/auth/auth.service';

@Injectable()
export class MenuService {

    public menuItems: Array<any>;

    // Permissions
    public canEditTeams = false;
    public canEditUsers = false;
    public canEditRoles = false;
    public canEditTemplates = false;
    public canAccessSurveys = false;
    public canEditPublicPages = false;
    public canAccessReports = false;
    public canAccessIntegration = false;
    public canAccessCampaigns = false;
    public canAccessLibrary = false;
    public hasBrandingPermission = false;
    public hasEventCustomFieldsPermission = false;
    public canAccessAllUserExports = false;
    public canAccessPayments = false;
    public hasInvitedeskEngagePermission = false;
    public tenantId: string;

    private readonly engagePermissionLoadedSubject = new ReplaySubject<boolean>(1);
    public readonly engagePermissionLoaded$: Observable<boolean> = this.engagePermissionLoadedSubject.asObservable();

    constructor(
        private permissions: PermissionService,
        private invitedeskApi: InvitedeskApiService,
        private dashboardApi: DashboardApiService,
        private styleService: StyleService,
        private authService: AuthService
    ) { }

    public buildMenu(): Array<any> {
        this.menuItems = [];
        this.invitedeskApi.settings
            .getPermission()
            .subscribe(
                (response: SuccessResponseLegacy<IPermission>) => {
                    // Set roles permissions
                    this.canEditTeams = this.permissions.hasPermission('team.edit');
                    this.canEditUsers = this.permissions.hasPermission('user.edit');
                    this.canEditRoles = this.permissions.hasPermission('profile.edit');
                    this.canEditTemplates = this.permissions.hasPermission('template.edit');
                    this.canAccessSurveys = this.permissions.hasPermission('survey.allow');
                    this.canEditPublicPages = this.permissions.hasPermission('publicpage.edit');
                    this.canAccessReports = this.permissions.hasPermission('report.allow');
                    this.canAccessIntegration = this.permissions.hasPermission('integration.allow') || this.permissions.hasPermission('aadsettings.manage');
                    this.canAccessLibrary = response?.data?.customFieldTemplatePermission || response?.data?.surveyPermission || response?.data?.documentPermission;
                    this.canAccessAllUserExports = this.permissions.hasPermission('report.viewalluserexports');
                    this.tenantId = this.authService.retrieveToken()?.decoded?.ten;

                    // Set tenant permissions
                    this.canAccessCampaigns = response?.data?.campaignPermission;
                    this.canAccessPayments = response?.data?.paydeskPermission &&
                        (this.permissions.hasPermission('paydesk.edit') || this.permissions.hasPermission('paydesk.document.view'));
                    this.hasInvitedeskEngagePermission = response?.data?.invitedeskEngagePermission ?? false;
                    this.engagePermissionLoadedSubject.next(this.hasInvitedeskEngagePermission);

                    // Load custom fonts
                    if (response?.data?.customFontPermission) {
                        this.getCustomFonts();
                    }
                    // Load custom branding style
                    if (response?.data?.brandingPermission) {
                        this.hasBrandingPermission = true;
                        this.getBrandingSettings();
                    }
                    if (response?.data?.eventCustomFieldsPermission) {
                        this.hasEventCustomFieldsPermission = true;
                    }

                    this.addMenu(menu);
                },
                (error: ErrorResponseLegacy) => {
                    this.engagePermissionLoadedSubject.next(false);
                    console.error('show notification', error.error);
                }
            );
        return this.menuItems;
    }

    private addMenu(items: Array<{
        text: string;
        heading?: boolean;
        link?: string;    // internal route links
        elink?: string;    // used only for external links
        target?: string;   // anchor target="_blank|_self|_parent|_top|framename"
        icon?: string;
        alert?: string;
        submenu?: Array<any>;
    }>) {
        items.forEach((item) => {
            // Hide route
            if (item.link && item.link === '/usersteams') {
                if (this.canEditRoles || this.canEditTeams || this.canEditUsers) {
                    this.menuItems.push(item);
                }
            } else if (item.link && item.link === '/templates') {
                if (this.canEditTemplates) {
                    this.menuItems.push(item);
                }
            } else if (item.link && item.link === '/surveys') {
                if (this.canAccessSurveys) {
                    this.menuItems.push(item);
                }
            } else if (item.link && item.link === '/publicpages') {
                if (this.canEditPublicPages) {
                    this.menuItems.push(item);
                }
            } else if (item.submenu?.some(el => el.link === '/reports')) {
                if (this.canAccessReports) {
                    this.menuItems.push(item);
                }
            } else if (item.link && item.link === '/integrations') {
                if (this.canAccessIntegration) {
                    this.menuItems.push(item);
                }
            } else if (item.link && item.link === '/campaigns') {
                if (this.canAccessCampaigns) {
                    this.menuItems.push(item);
                }
            } else if (item.link && item.link === '/library') {
                if (this.canAccessLibrary) {
                    this.menuItems.push(item);
                }
            } else if (item.elink && item.elink.includes('payments')) {
                if (this.canAccessPayments) {
                    item.elink = item.elink
                        .replace('{tenantid}', this.tenantId);
                    this.menuItems.push(item);
                }
            } else {
                this.menuItems.push(item);
            }
        });
    }

    private getCustomFonts() {
        this.dashboardApi.customFonts
            .getCustomFonts()
            .subscribe(
                (response: SuccessResponse<ICustomFonts>) => {
                    // Load custom fonts
                    response?.data?.customFonts?.forEach(customFont => {
                        this.styleService.loadCustomFont(customFont?.displayName, customFont?.url);
                    });
                },
                (error: ErrorResponse) => {
                    console.error('show notification', error?.messages);
                }
            );
    }

    public getBrandingSettings() {
        this.dashboardApi.branding
            .getBrandingSettings()
            .subscribe(
                (response: SuccessResponse<IBrandingStyleResponse>) => {
                    if (response.data) {
                        this.styleService.setBrandingStyle(response.data);
                    }
                },
                (error: ErrorResponse) => {
                    console.error('show notification', error?.messages);
                }
            );
    }

}
